import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { LineReveal, FadeUp } from "../components/RevealText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Commercial HVAC Install",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: SERVICES[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and a short message.");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/contact`, form);
      if (data && data.id) {
        toast.success("Received. A project manager will reach out.");
        setForm({
          name: "",
          company: "",
          email: "",
          phone: "",
          service: SERVICES[0],
          message: "",
        });
      } else {
        toast.error("Unexpected response. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Call us at 631.643.3433.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-black text-white pt-24">
      <Toaster position="bottom-right" theme="dark" />

      {/* Header */}
      <section className="border-b border-white/10 px-4 md:px-6 py-12 md:py-16">
        <h1 className="font-display font-bold text-6xl md:text-8xl leading-[0.9] uppercase">
          <LineReveal delay={0.15}>Contact</LineReveal>
        </h1>
      </section>

      {/* Split */}
      <section className="grid grid-cols-12 border-b border-white/10">
        {/* Left – Form */}
        <div className="col-span-12 md:col-span-7 border-white/10 md:border-r p-6 md:p-12">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-8">
              [ Project intake · Response within 24h ]
            </p>
          </FadeUp>

          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-2 gap-6">
              <Field
                label="Name"
                value={form.name}
                onChange={handle("name")}
                testid="input-name"
                required
              />
              <Field
                label="Company"
                value={form.company}
                onChange={handle("company")}
                testid="input-company"
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={handle("email")}
                testid="input-email"
                required
              />
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={handle("phone")}
                testid="input-phone"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                Service
              </label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, service: s }))}
                    className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-500 ${
                      form.service === s
                        ? "bg-white text-black border-white"
                        : "border-white/20 text-white/70 hover:border-white hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                Message *
              </label>
              <textarea
                value={form.message}
                onChange={handle("message")}
                data-testid="input-message"
                rows={5}
                placeholder="Scope, tonnage, site address, timeline…"
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-lg text-white placeholder:text-white/25 transition-colors duration-500 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-contact"
              className="group inline-flex items-center gap-4 border border-white bg-white text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors duration-500 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Submit inquiry"}
              <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                →
              </span>
            </button>
          </form>
        </div>

        {/* Right – Meta / Map */}
        <div className="col-span-12 md:col-span-5 p-6 md:p-12 bg-black">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
              [ 01 · Address ]
            </p>
            <p className="font-display text-4xl md:text-5xl leading-tight mb-2">
              440 Wyandanch Avenue
            </p>
            <p className="font-display text-2xl md:text-3xl text-white/60">
              North Babylon, NY 11704
            </p>
          </FadeUp>

          <div className="grid grid-cols-2 gap-6 mt-12">
            <FadeUp delay={0.1}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Phone
              </p>
              <a
                href="tel:+16316433433"
                data-testid="contact-phone-link"
                className="font-display text-2xl hover:text-white/80 transition-colors"
              >
                631.643.3433
              </a>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Email
              </p>
              <a
                href="mailto:info@anronac.com"
                data-testid="contact-email-link"
                className="font-display text-2xl hover:text-white/80 transition-colors"
              >
                info@anronac.com
              </a>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Fax
              </p>
              <p className="font-display text-2xl text-white/80">631.491.6983</p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Hours
              </p>
              <p className="text-white text-lg">Mon-Fri · 8:00 - 5:00</p>
            </FadeUp>
          </div>

          {/* Map */}
          <FadeUp delay={0.3} className="mt-12">
            <div className="relative aspect-[4/3] border border-white/10 overflow-hidden">
              <iframe
                title="Anron Air Systems location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=440+Wyandanch+Avenue,+North+Babylon,+NY+11704&output=embed"
                className="absolute inset-0 w-full h-full grayscale contrast-90"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
        {label}
        {required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-lg text-white placeholder:text-white/25 transition-colors duration-500"
      />
    </div>
  );
}
